import React, { useEffect, useState } from 'react'
import PastOrdersContainer from './container'
import { Box, Button, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { currencySymbol, ordersFilterConfigs, statusConfigs, statusOptions } from 'utils/constant';
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useGetPastOrdersQuery } from './services';
import { useNavigate } from 'react-router-dom';
import { addDays, startOfMonth, endOfMonth, subMonths, format, startOfWeek, endOfWeek, subWeeks } from 'date-fns';


const PastOrders = () => {
    const userData = useSelector((state) => state?.posData);
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [paginationModel, setPaginationModel] = useState({
        page: window.history.state?.prevPage ?? 0,
        pageSize: 10
    });
    const [filter, setFilter] = useState(() => window.history.state?.prevFilter ?? 'All');
    const [pickerOpen, setPickerOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectionRange, setSelectionRange] = useState({
        startDate: null,
        endDate: null,
        key: 'selection',
        color: '#000'
    });

    useEffect(() => {
        const handlePopState = (event) => {
            const historyState = event.state;

            if (historyState?.prevPage !== undefined) {
                setFilter(historyState.prevFilter);

                setPaginationModel((prev) => ({
                    ...prev,
                    page: historyState.prevPage
                }));
            }
        };

        window.addEventListener("popstate", handlePopState);

        return () => {
            window.removeEventListener("popstate", handlePopState);
        };
    }, []);

    // debounce searchTerm → debouncedSearch
    useEffect(() => {
        const timeout = setTimeout(() => {
            setDebouncedSearch(searchTerm.trim());
        }, 500);
        return () => clearTimeout(timeout);
    }, [searchTerm]);
    const shouldSearch = debouncedSearch.length >= 3 ? debouncedSearch : '';

    const { data: resp, isLoading, isError, isFetching } = useGetPastOrdersQuery({
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        order_type: filter,
        search: shouldSearch,
        start_date: selectionRange?.startDate ? dayjs(selectionRange.startDate).startOf('day').toISOString() : '',
        end_date: selectionRange?.endDate ? dayjs(selectionRange.endDate).endOf('day').toISOString() : '',
    }, { refetchOnMountOrArgChange: true });

    // pull out records / total count safely
    const records = resp?.data?.records || [];
    const totalCount = resp?.data?.totalCount || 0;
    const orderTypeFlags = resp?.data?.state?.order_type_flags ?? resp?.data?.stats?.order_type_flags ?? resp?.data?.order_type_flags;

    const handleDetails = (row) => {
        window.history.replaceState(
            {
                ...window.history.state,
                prevFilter: filter,
                prevPage: paginationModel.page
            },
            ""
        );
        navigate("/order-details", { state: { orderId: row?.id, isViewOnly: true, prevFilter: filter, prevPage: paginationModel.page } });
    }

    const columns = [
        {
            headerName: 'Order #',
            field: 'order_id',
            flex: 1,
            minWidth: 180,
            renderHeader: () => (
                <Box sx={{ pl: '15px', fontWeight: 600 }}>Order #</Box>
            ),
            renderCell: ({ row }) => <Box sx={{ paddingLeft: '15px' }}>#{row.order_id}</Box>,
        },
        {
            headerName: 'Ordered at',
            field: 'created_at',
            flex: 1,
            minWidth: 180,
            renderCell: ({ row }) => {
                // Parse the full ISO timestamp
                const created = dayjs(row.created_at);

                // If it's today, show "TODAY"; otherwise format as "DD MMM YYYY"
                const isToday = created.isSame(dayjs(), 'day');
                const displayDate = isToday
                    ? 'TODAY'
                    : created.format('DD MMM YYYY');

                // Format time as "hh:mm A"
                const displayTime = created.format('hh:mm A');

                return (
                    <Box sx={{ alignContent: 'space-evenly', height: '100%' }}>
                        <Typography variant='body2'>{displayDate}</Typography>
                        <Typography variant="body2">
                            {displayTime}
                        </Typography>
                    </Box>
                );
            },
        },
        {
            headerName: 'Order Type',
            field: 'order_type',
            flex: 1,
            minWidth: 140,
            renderCell: ({ row }) => {
                const cfg = ordersFilterConfigs.find((f) => f.id === row.order_type) || {
                    label: row.order_type,
                    color: '#000',
                    backgroundColor: '#e0e0e0',
                };
                // const collectionName = row?.collectionPoint?.name
                return (
                    <Box sx={{ height: "100%", alignContent: "center", paddingLeft: "10px" }}>
                        <Typography variant='body2'
                            sx={{
                                color: cfg.color,
                                fontWeight: 500,
                                // height: '100%',
                                alignContent: 'space-evenly'
                            }}
                        >
                            {cfg.label}
                        </Typography>
                        {/* {
                            row.order_type == "collection_point" &&
                            <Typography color={cfg.color} fontSize="12px">({collectionName})</Typography>
                        } */}
                        {
                            row.order_type == "dine_in" &&
                            <Typography fontSize="12px">({row?.table_name})</Typography>
                        }
                    </Box>
                );
            },
        },
        {
            headerName: 'Customer',
            field: 'customer',
            flex: 1,
            minWidth: 200,
            renderCell: ({ row }) => {
                const info = row.customerVendor?.customerInfo || {};
                const name = row.customerVendor?.customer_full_name || row.customer_name;
                const phone = info.phone || row.customer_mobile;
                const countryCode = info.country_code || '';
                return (
                    <Box sx={{ alignContent: 'space-evenly', height: '100%' }}>
                        <Typography variant='body2' sx={{ fontWeight: 500 }}>{name || '-'}</Typography>
                        {phone ? (
                            <Typography variant='body2'>
                                {countryCode} {phone}
                            </Typography>
                        ) : null}
                    </Box>
                );
            },
        },
        {
            headerName: 'Order Total',
            field: 'total_amount',
            flex: 1,
            minWidth: 120,
            renderCell: ({ row }) => (
                <Box sx={{ alignContent: 'space-evenly', height: '100%' }}>
                    <Typography variant='body2'>{currencySymbol(userData?.vendor?.currency)}
                        {(
                            parseFloat(row?.total_amount || 0) +
                            (row?.rider_tip_amount > 0 && row?.order_type === 'delivery'
                                ? parseFloat(row?.rider_tip_amount || 0)
                                : 0)
                        ).toFixed(2)}
                    </Typography>
                    <Typography variant='body2'>{`(${row?.total_item_count} Items)`}</Typography>
                </Box>
            ),
        },
        {
            headerName: 'Status',
            field: 'order_status',
            flex: 1,
            minWidth: 140,
            renderCell: ({ row }) => {
                const current = row.order_status;
                const flow = statusOptions[row.order_type] || [];
                const next = flow[current] || [];
                return (
                    <Box className='d-flex align-items-center' sx={{ height: '100%' }}>
                        <Typography variant='body2' sx={{ padding: '0px 10px', alignContent: 'center', lineHeight: '1.5', backgroundColor: statusConfigs[current].bg, color: '#000', borderRadius: '8px', fontWeight: '600', width: '140px', height: '31px', fontSize: '13px' }}>
                            {statusConfigs[current].label}
                        </Typography>
                    </Box>
                );
            },
        },
        {
            headerName: '',
            field: 'actions',
            minWidth: 150,
            renderCell: ({ row }) => (
                <Box className='d-flex align-items-center' sx={{ gap: 2, height: '100%' }}>
                    <Button size="small" variant='outlined' onClick={() => handleDetails(row)} endIcon={<ArrowForwardIcon />} sx={{ textTransform: 'none' }}>
                        Details
                    </Button>
                </Box>
            ),
        },
    ];

    const handleBack = () => {
        navigate("/orders")
    }
    const presets = [
        {
            label: 'Current Week',
            range: {
                startDate: startOfWeek(new Date(), { weekStartsOn: 1 }),
                endDate: endOfWeek(new Date(), { weekStartsOn: 1 }),
            }
        },
        {
            label: 'Previous Week',
            range: {
                startDate: startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }),
                endDate: endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }),
            }
        },
        {
            label: 'Current Month',
            range: {
                startDate: startOfMonth(new Date()),
                endDate: endOfMonth(new Date())
            }
        },
        {
            label: 'Previous Month',
            range: {
                startDate: startOfMonth(subMonths(new Date(), 1)),
                endDate: endOfMonth(subMonths(new Date(), 1))
            }
        }
    ];

    const handleButtonClick = (e) => {
        setAnchorEl(e.currentTarget);
        setPickerOpen(open => !open);
    };

    const handleSelect = (ranges) => {
        const { startDate, endDate } = ranges.selection;
        // setSelectionRange({ ...ranges.selection, color: '#3d91ff' });
        setSelectionRange({ ...ranges.selection, color: '#000' });

        // only close when the end date has changed from the start date
        // if (endDate.getTime() !== startDate.getTime()) {
        //     setPickerOpen(false);
        // }

        // close the picker
        setPickerOpen(false);
    };



    return (
        <PastOrdersContainer
            userData={userData}
            setFilter={setFilter}
            filter={filter}
            columns={columns}
            rows={records}
            totalCount={totalCount}
            loading={isLoading}
            searchQuery={searchTerm}
            setSearchQuery={setSearchTerm}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            searchLoader={isFetching}
            handleBack={handleBack}

            presets={presets}
            handleSelect={handleSelect}
            handleButtonClick={handleButtonClick}
            selectionRange={selectionRange}
            setSelectionRange={setSelectionRange}
            pickerOpen={pickerOpen}
            anchorEl={anchorEl}
            setPickerOpen={setPickerOpen}
            orderTypeFlags={orderTypeFlags}
        />
    )
}

export default PastOrders
